const SQRT2 = Math.SQRT2;
/** Neighbour offsets: 4 orthogonal first, then 4 diagonal. */
const DX = [1, -1, 0, 0, 1, 1, -1, -1];
const DY = [0, 0, 1, -1, 1, -1, 1, -1];
const COST = [1, 1, 1, 1, SQRT2, SQRT2, SQRT2, SQRT2];
/**
 * A* on an 8-neighbour grid with no corner cutting (delegated to
 * TileGrid.canStep, which also respects edge walls).
 *
 * All per-search state lives in typed arrays sized to the grid and reused
 * across searches; "generation" stamps avoid clearing them each call, so a
 * search allocates only its result array.
 */
export class AStar {
    grid;
    g;
    f;
    parent;
    /** Generation stamp: node seen (g valid) in this search. */
    seen;
    /** Generation stamp: node closed in this search. */
    closed;
    heap;
    heapSize = 0;
    gen = 0;
    /** Expansions performed by the last search (for stats/tests). */
    lastExpansions = 0;
    constructor(grid) {
        this.grid = grid;
        const n = grid.width * grid.height;
        this.g = new Float64Array(n);
        this.f = new Float64Array(n);
        this.parent = new Int32Array(n);
        this.seen = new Uint32Array(n);
        this.closed = new Uint32Array(n);
        // A node can be pushed more than once (lazy decrease-key), so allow 8 pushes per node worst case.
        this.heap = new Int32Array(n * 8 + 8);
    }
    /**
     * Returns the steps from start (exclusive) to goal (inclusive), [] if
     * start === goal, or null if there is no path (unless allowPartial).
     */
    findPath(sx, sy, gx, gy, opts = {}) {
        const grid = this.grid;
        const W = grid.width;
        if (!grid.inBounds(sx, sy) || !grid.inBounds(gx, gy))
            return null;
        if (sx === gx && sy === gy)
            return [];
        const goalWalkable = grid.isWalkable(gx, gy);
        if (!goalWalkable && !opts.allowPartial)
            return null;
        const maxExp = opts.maxExpansions ?? 6000;
        this.gen++;
        if (this.gen === 0xffffffff) {
            this.seen.fill(0);
            this.closed.fill(0);
            this.gen = 1;
        }
        const gen = this.gen;
        const { g, f, parent, seen, closed } = this;
        this.heapSize = 0;
        const start = sy * W + sx;
        const goal = gy * W + gx;
        g[start] = 0;
        f[start] = this.h(sx, sy, gx, gy);
        parent[start] = -1;
        seen[start] = gen;
        this.push(start);
        let best = start;
        let bestH = f[start];
        let expansions = 0;
        while (this.heapSize > 0) {
            const cur = this.pop();
            if (closed[cur] === gen)
                continue; // stale heap entry
            closed[cur] = gen;
            if (cur === goal) {
                this.lastExpansions = expansions;
                return this.reconstruct(goal);
            }
            if (++expansions > maxExp)
                break;
            const cx = cur % W;
            const cy = (cur - cx) / W;
            const hCur = f[cur] - g[cur];
            if (hCur < bestH) {
                bestH = hCur;
                best = cur;
            }
            for (let k = 0; k < 8; k++) {
                const dx = DX[k];
                const dy = DY[k];
                if (!grid.canStep(cx, cy, dx, dy))
                    continue;
                const nx = cx + dx;
                const ny = cy + dy;
                const ni = ny * W + nx;
                if (closed[ni] === gen)
                    continue;
                const ng = g[cur] + COST[k];
                if (seen[ni] !== gen || ng < g[ni]) {
                    seen[ni] = gen;
                    g[ni] = ng;
                    f[ni] = ng + this.h(nx, ny, gx, gy);
                    parent[ni] = cur;
                    this.push(ni);
                }
            }
        }
        this.lastExpansions = expansions;
        if (opts.allowPartial && best !== start)
            return this.reconstruct(best);
        return null;
    }
    /** Octile distance, scaled by 1.001 to break ties toward the goal (fewer expansions, still near-optimal). */
    h(x, y, gx, gy) {
        const dx = Math.abs(x - gx);
        const dy = Math.abs(y - gy);
        return (dx + dy + (SQRT2 - 2) * Math.min(dx, dy)) * 1.001;
    }
    reconstruct(end) {
        const W = this.grid.width;
        const out = [];
        let n = end;
        while (this.parent[n] !== -1) {
            const x = n % W;
            out.push({ x, y: (n - x) / W });
            n = this.parent[n];
        }
        out.reverse();
        return out;
    }
    // Binary min-heap on f[] -------------------------------------------------
    push(node) {
        const heap = this.heap;
        const f = this.f;
        let i = this.heapSize++;
        heap[i] = node;
        const fn = f[node];
        while (i > 0) {
            const p = (i - 1) >> 1;
            const pn = heap[p];
            if (f[pn] <= fn)
                break;
            heap[i] = pn;
            i = p;
        }
        heap[i] = node;
    }
    pop() {
        const heap = this.heap;
        const f = this.f;
        const top = heap[0];
        const last = heap[--this.heapSize];
        const n = this.heapSize;
        if (n > 0) {
            let i = 0;
            const fl = f[last];
            for (;;) {
                const l = i * 2 + 1;
                if (l >= n)
                    break;
                const r = l + 1;
                let c = l;
                if (r < n && f[heap[r]] < f[heap[l]])
                    c = r;
                if (f[heap[c]] >= fl)
                    break;
                heap[i] = heap[c];
                i = c;
            }
            heap[i] = last;
        }
        return top;
    }
}
